import {Repository} from "./Repository";
import {AxiosInstance} from "axios";
import {CommentQueryParametersInterface} from "./interfaces/CommentQueryParametersInterface";
import {CommentsBlock} from "../models/comments/CommentsBlock";
import {Comment} from "../models/comments/Comment";
import {CommentInterface} from "../models/interfaces/comments/CommentInterface";
import {CommentsCountInterface} from "@api/models/interfaces/comments/CommentsCountInterface";
import { CommentType, CommentAttachment } from "@api/enums/CommentTypes";

export class CommentRepository implements Repository {
    client: AxiosInstance;

    constructor(client: AxiosInstance) {
        this.client = client;
    }

    query(schemaId: string, objectId: string, query: CommentQueryParametersInterface = {}): Promise<CommentsBlock> {
        return this.client.post<CommentsBlock>(`/comments/${schemaId}/${objectId}/query`, query)
            .then(({data}) => {
                return new CommentsBlock(data);
            })
    }

    sendComment(schemaId: string, objectId: string, message: string, type: CommentType, attachments: CommentAttachment[] = []): Promise<Comment> {
        const body: any = {
            text: message,
            attachments: attachments
        };
        if (type) {
            body.type = type;
        }
        return this.client.post<Comment>(`/v2/comments/${schemaId}/${objectId}`, body)
            .then(({data}) => {
                return new Comment(data);
            })
    }

    sendCommentAnswer(schemaId: string, objectId: string, commentId: string, message: string): Promise<Comment> {
        return this.client.post<Comment>(`/comments/${schemaId}/${objectId}/${commentId}`, message)
            .then(({data}) => {
                return new Comment(data);
            })
    }

    edit(commentId: string, message: string) {
        return this.client.put<Comment>(`v2/comments/${commentId}`, { text: message});
    }

    setLike(schemaId: string, objectId: string, commentId: string, value: number | null): Promise<void> {
        return this.client.post(`/comments/${schemaId}/${objectId}/${commentId}/likes`, value)
    }

    count(schemaId: string, ids: string[]): Promise<CommentsCountInterface[]> {
        return this.client.post<CommentsCountInterface[]>(`/comments/${schemaId}/query?withDeleted=false`, ids)
            .then(({data}) =>{
                return data;
            })
    }

    updates(schemaId: string, objectId: string, lastUpdatedAt: string): Promise<{ updated: Comment[] | null; deleted: string[] | null; updatedAt: string }> {
        return this.client.get(`/comments/${schemaId}/${objectId}/updates?lastUpdatedAt=${encodeURIComponent(lastUpdatedAt)}`)
            .then(({data}) =>{
                if (data.updated) {
                    data.updated = data.updated.map((item: CommentInterface) => new Comment(item))
                }
                return data;
            })
    }

    delete(commentId: string): Promise<void> {
        return this.client.delete(`/comments/${commentId}`);
    }

    countViaQueryMethod(schemaId: string, ids: string[]) {
        return this.client.post(`/comments/query?count=true`, {
            where: {
                schemaId: schemaId,
                objectId: {
                    '$in': ids
                }
            },
            take: 0
        })
            .then(({headers}) =>{
                return parseInt(headers['x-appercode-totalitems'] || 0);
            })
    }

    pin(commentId: string): Promise<void> {
        return this.client.put(`/v2/comments/${commentId}`, { isPinned: true });
    }

    unpin(commentId: string): Promise<void> {
        return this.client.put(`/v2/comments/${commentId}`, { isPinned: false });
    }

    publish(commentId: string): Promise<void> {
        return this.client.put(`/v2/comments/${commentId}`, { isPublished: true });
    }

    unpublish(commentId: string): Promise<void> {
        return this.client.put(`/v2/comments/${commentId}`, { isPublished: false });
    }
}
